import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_DATE_STYLE, isDateStyle, setDateStyle, type DateStyle } from "@shared/dates";

export type Mode = "day" | "night";
export type Density = "comfortable" | "compact";
export type TablePrefs = { density: Density; hidden: string[] };

type UiState = {
  mode: Mode;
  /** How dates are written everywhere on this device (see DATE_STYLES). */
  dateStyle: DateStyle;
  /** Property id, or "all" for the roll-up. */
  scope: string;
  /** Per table (keyed by route, or route and name): row density and hidden columns. */
  tables: Record<string, TablePrefs>;
  /** The section each area was last on, this session: clicking the area returns there. */
  lastSection: Record<string, string>;
  /** Upkeep's and Settings' section rail folded to icons. Null until the person chooses: then it follows the screen width. */
  railCollapsed: boolean | null;
  setMode: (m: Mode) => void;
  toggleMode: () => void;
  setDateStyleTo: (s: DateStyle) => void;
  setScope: (s: string) => void;
  setTablePrefs: (key: string, patch: Partial<TablePrefs>) => void;
  rememberSection: (area: string, path: string) => void;
  setRailCollapsed: (c: boolean) => void;
};

export const DEFAULT_TABLE_PREFS: TablePrefs = { density: "comfortable", hidden: [] };

function applyMode(m: Mode) {
  document.documentElement.dataset.mode = m;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", m === "night" ? "#161A1F" : "#FFFDF8");
  try {
    localStorage.setItem("kk-mode", m);
  } catch {
    // Private windows and blocked site data: the mode just won't be remembered.
  }
}

export const useUi = create<UiState>()(
  persist(
    (set, get) => ({
      mode: (document.documentElement.dataset.mode as Mode) ?? "day",
      scope: "all",
      tables: {},
      lastSection: {},
      railCollapsed: null,
      setMode: (m) => {
        applyMode(m);
        set({ mode: m });
      },
      toggleMode: () => get().setMode(get().mode === "day" ? "night" : "day"),
      dateStyle: DEFAULT_DATE_STYLE,
      setDateStyleTo: (dateStyle) => {
        setDateStyle(dateStyle);
        set({ dateStyle });
      },
      setScope: (scope) => set({ scope }),
      setTablePrefs: (key, patch) => set((s) => ({ tables: { ...s.tables, [key]: { ...DEFAULT_TABLE_PREFS, ...s.tables[key], ...patch } } })),
      setRailCollapsed: (railCollapsed) => set({ railCollapsed }),
      rememberSection: (area, path) => set((s) => (s.lastSection[area] === path ? s : { lastSection: { ...s.lastSection, [area]: path } })),
    }),
    {
      name: "kk-ui",
      partialize: (s) => ({ scope: s.scope, mode: s.mode, dateStyle: s.dateStyle, tables: s.tables, railCollapsed: s.railCollapsed }),
      onRehydrateStorage: () => (s) => {
        if (!s) return;
        applyMode(s.mode);
        // A style from an older build that no longer exists falls back to the house one.
        setDateStyle(isDateStyle(s.dateStyle) ? s.dateStyle : DEFAULT_DATE_STYLE);
      },
    },
  ),
);
